import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from "lucide-react";

const cop = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const num = (n) => Number(n || 0).toLocaleString("es-CO", { maximumFractionDigits: 1 });
const pct = (n) => (n == null ? "—" : `${Number(n).toLocaleString("es-CO", { maximumFractionDigits: 1 })}%`);

/** Finanzas → "¿Por qué bajó la utilidad?" — descompone el costo del periodo por contenedor. */
export default function DiagnosticoUtilidadPanel({ desde, hasta }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/reportes/diagnostico-utilidad", { params: { desde: desde || undefined, hasta: hasta || undefined } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo generar el diagnóstico"))
      .finally(() => setLoading(false));
  }, [open, desde, hasta]);

  const k = data?.kpis;
  return (
    <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="diagnostico-utilidad-panel">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 text-left" data-testid="diagnostico-utilidad-toggle">
        <div className="flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-primary" />
          <div>
            <div className="text-sm font-semibold text-foreground">¿Por qué bajó la utilidad? · Diagnóstico por contenedor</div>
            <div className="text-xs text-muted-foreground">De dónde sale el costo de lo vendido en este periodo, qué contenedor está caro, lotes con costo cerca del precio mínimo, doble cargo de gastos y gastos duplicados.</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          {loading && <div className="text-sm text-muted-foreground">Analizando pedidos, lotes y despachos…</div>}
          {data && k && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                <Kpi label="Ventas" value={cop(k.ventas)} sub={`${k.pedidos} pedidos`} testid="diag-ventas" />
                <Kpi label="Costo de lo vendido" value={cop(k.cogs)} sub={`${pct(k.costo_sobre_venta_pct)} de la venta`} warn={(k.costo_sobre_venta_pct || 0) > 50} testid="diag-cogs" />
                <Kpi label="Comisiones" value={cop(k.comisiones)} sub={`${pct(k.comision_pct)} de la venta`} testid="diag-comisiones" />
                <Kpi label="Gastos del mes" value={cop(k.gastos)} sub="gastos mensuales registrados" testid="diag-gastos" />
                <Kpi label="Utilidad bruta" value={cop(k.utilidad_bruta)} sub="ventas − costo" testid="diag-ub" />
                <Kpi label="Utilidad neta" value={cop(k.utilidad_neta)} sub="− comisiones − gastos" warn={k.utilidad_neta < 0} testid="diag-un" />
              </div>
              <div className="text-[11px] text-muted-foreground">
                Referencia histórica que mencionaste: costo ≈ 45% de la venta, comisiones ≈ 9%, utilidad ≈ 45%. Hoy: costo <b>{pct(k.costo_sobre_venta_pct)}</b>, comisiones <b>{pct(k.comision_pct)}</b>.
                {k.cogs_snapshot_sin_despachar > 0 && <> · {cop(k.cogs_snapshot_sin_despachar)} del costo corresponde a pedidos confirmados aún sin despachar (costo de ficha).</>}
              </div>

              {data.alertas?.length > 0 ? (
                <div className="space-y-1.5" data-testid="diag-alertas">
                  {data.alertas.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs rounded-md border border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 px-3 py-2">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-600 flex-shrink-0" />
                      <div><b>{a.contenedor ? `${a.contenedor}: ` : ""}</b>{a.detalle}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="diag-sin-alertas"><CheckCircle2 className="w-3.5 h-3.5" /> No se detectaron dobles cargos ni lotes con costo sobre el precio mínimo.</div>
              )}

              <Section title="Costo de lo vendido por contenedor de origen (FIFO real de los despachos)">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/50"><tr><th className="text-left p-2">Contenedor</th><th className="text-right p-2">Unid. vendidas</th><th className="text-right p-2">Venta atribuida</th><th className="text-right p-2">Costo</th><th className="text-right p-2">Costo / venta</th><th className="text-right p-2">Margen</th></tr></thead>
                  <tbody>
                    {data.cogs_por_contenedor.map((r) => (
                      <tr key={r.contenedor} className="border-t border-border/60 tabular-nums" data-testid={`diag-cont-${r.contenedor}`}>
                        <td className="p-2 font-medium">{r.contenedor}</td><td className="p-2 text-right">{num(r.unidades)}</td><td className="p-2 text-right">{cop(r.ventas)}</td><td className="p-2 text-right">{cop(r.cogs)}</td>
                        <td className={`p-2 text-right ${(r.costo_sobre_venta_pct || 0) > 55 ? "text-rose-600 font-semibold" : ""}`}>{pct(r.costo_sobre_venta_pct)}</td>
                        <td className={`p-2 text-right ${(r.margen_pct || 0) < 35 ? "text-rose-600 font-semibold" : "text-emerald-700"}`}>{pct(r.margen_pct)}</td>
                      </tr>
                    ))}
                    {data.cogs_por_contenedor.length === 0 && <tr><td className="p-2 text-muted-foreground" colSpan={6}>Sin despachos en el periodo.</td></tr>}
                  </tbody>
                </table>
              </Section>

              <Section title="Cómo quedó costeado cada contenedor (lotes)">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/50"><tr><th className="text-left p-2">Contenedor</th><th className="text-right p-2">Lotes</th><th className="text-right p-2">Valor recibido</th><th className="text-right p-2">Valor disponible</th><th className="text-right p-2">Costo / precio mín. (prom)</th><th className="text-right p-2">Lotes ≥ mínimo</th><th className="text-right p-2">Con landed</th><th className="text-right p-2">Landed + % gastos</th></tr></thead>
                  <tbody>
                    {data.lotes_por_contenedor.map((r) => (
                      <tr key={r.contenedor} className="border-t border-border/60 tabular-nums">
                        <td className="p-2 font-medium">{r.contenedor}</td><td className="p-2 text-right">{r.lotes}</td><td className="p-2 text-right">{cop(r.valor_recibido)}</td><td className="p-2 text-right">{cop(r.valor_disponible)}</td>
                        <td className={`p-2 text-right ${(r.costo_sobre_minimo_prom_pct || 0) > 65 ? "text-rose-600 font-semibold" : ""}`}>{pct(r.costo_sobre_minimo_prom_pct)}</td>
                        <td className={`p-2 text-right ${r.lotes_costo_sobre_minimo ? "text-rose-600 font-semibold" : ""}`}>{r.lotes_costo_sobre_minimo}</td>
                        <td className="p-2 text-right">{r.lotes_con_landed}</td>
                        <td className={`p-2 text-right ${r.lotes_landed_mas_pct ? "text-rose-600 font-semibold" : ""}`}>{r.lotes_landed_mas_pct}{r.recargo_gastos_prom_pct ? ` (+${pct(r.recargo_gastos_prom_pct)})` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="text-[10px] text-muted-foreground mt-1">"Costo / precio mín." alto (≥ 65%) significa que al vender al mínimo la utilidad bruta queda por debajo del 35%. "Landed + % gastos" = lotes donde se sumaron los gastos reales de importación Y además el % de gastos (doble cargo).</div>
              </Section>

              {data.contenedores?.length > 0 && (
                <Section title="Parámetros usados al importar cada contenedor">
                  <table className="w-full text-[11px]">
                    <thead className="bg-muted/50"><tr><th className="text-left p-2">Referencia</th><th className="text-right p-2">TRM</th><th className="text-right p-2">% gastos</th><th className="text-right p-2">% margen mín.</th><th className="text-right p-2">% margen sug.</th><th className="text-right p-2">Gastos landed (COP)</th></tr></thead>
                    <tbody>
                      {data.contenedores.map((c, i) => (
                        <tr key={i} className="border-t border-border/60 tabular-nums"><td className="p-2 font-medium">{c.referencia}{c.origen === "borrador" ? " (borrador)" : ""}</td><td className="p-2 text-right">{num(c.trm_aplicada)}</td><td className={`p-2 text-right ${(c.pct_gastos_default || 0) > 0 && (c.gastos_landed_total_cop || 0) > 0 ? "text-rose-600 font-semibold" : ""}`}>{pct(c.pct_gastos_default)}</td><td className="p-2 text-right">{pct(c.pct_margen_minimo_default)}</td><td className="p-2 text-right">{pct(c.pct_margen_sugerido_default)}</td><td className="p-2 text-right">{c.gastos_landed_total_cop ? cop(c.gastos_landed_total_cop) : "—"}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </Section>
              )}

              {data.lotes_caros?.length > 0 && (
                <Section title={`Lotes con costo ≥ 70% del precio mínimo (${data.lotes_caros.length})`}>
                  <div className="max-h-72 overflow-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-2">Producto</th><th className="text-left p-2">Contenedor</th><th className="text-right p-2">Costo lote</th><th className="text-right p-2">Base</th><th className="text-right p-2">Landed</th><th className="text-right p-2">Costo ficha</th><th className="text-right p-2">Precio mín.</th><th className="text-right p-2">Costo/mín.</th><th className="text-right p-2">Disp.</th></tr></thead>
                      <tbody>
                        {data.lotes_caros.map((l) => (
                          <tr key={l.lote_id} className="border-t border-border/60 tabular-nums">
                            <td className="p-2 max-w-[220px]"><div className="truncate font-medium">{l.producto}</div><div className="text-muted-foreground">{l.codigo}</div></td>
                            <td className="p-2">{l.contenedor}</td><td className="p-2 text-right font-semibold">{cop(l.costo_unitario)}</td><td className="p-2 text-right">{l.costo_base_cop ? cop(l.costo_base_cop) : "—"}</td><td className="p-2 text-right">{l.landed_unit_cop ? cop(l.landed_unit_cop) : "—"}</td>
                            <td className="p-2 text-right">{cop(l.costo_ficha)}</td><td className="p-2 text-right">{cop(l.precio_minimo)}</td><td className={`p-2 text-right ${l.ratio >= 1 ? "text-rose-600 font-semibold" : ""}`}>{pct(l.ratio * 100)}</td><td className="p-2 text-right">{num(l.disponible)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}

              {data.ventas_bajo_costo?.length > 0 && (
                <Section title={`Despachos vendidos a precio ≤ costo (${data.ventas_bajo_costo.length})`}>
                  <div className="max-h-56 overflow-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-muted/50 sticky top-0"><tr><th className="text-left p-2">Producto</th><th className="text-left p-2">Contenedor</th><th className="text-right p-2">Cant.</th><th className="text-right p-2">Costo</th><th className="text-right p-2">Precio venta</th></tr></thead>
                      <tbody>{data.ventas_bajo_costo.map((v, i) => (<tr key={i} className="border-t border-border/60 tabular-nums"><td className="p-2"><div className="font-medium">{v.producto}</div><div className="text-muted-foreground">{v.codigo}</div></td><td className="p-2">{v.contenedor}</td><td className="p-2 text-right">{num(v.cantidad)}</td><td className="p-2 text-right text-rose-600 font-semibold">{cop(v.costo_unitario)}</td><td className="p-2 text-right">{cop(v.precio_unitario)}</td></tr>))}</tbody>
                    </table>
                  </div>
                </Section>
              )}

              {data.gastos_sospechosos?.length > 0 && (
                <Section title="Gastos mensuales que parecen ser el contenedor (ya está en el costo de los lotes)">
                  <ul className="text-xs space-y-1">{data.gastos_sospechosos.map((g, i) => <li key={i} className="flex justify-between"><span>{g.mes} · {g.concepto}</span><b>{cop(g.monto)}</b></li>)}</ul>
                </Section>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/30">{title}</div>
      <div className="p-1">{children}</div>
    </div>
  );
}

function Kpi({ label, value, sub, warn, testid }) {
  return (
    <div className={`rounded-lg border p-2.5 ${warn ? "border-rose-300 bg-rose-50/60 dark:bg-rose-900/15" : "border-border bg-muted/20"}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate" title={sub}>{sub}</div>}
    </div>
  );
}
